import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useProfile, type GamificationProfile } from '../hooks/useProfile';
import { useBackButton } from '../hooks/useBackButton';
import { haptic } from '../utils/haptic';
import { BadgesList } from './BadgesList';
import { StreakWidget } from './StreakWidget';
import { Skeleton } from './Skeleton';
import { IconPen, IconChevronRight } from './icons';
import { AvatarWithProgress } from './AvatarWithProgress';
import { useAppStore } from '../store/useAppStore';
import './ProfileScreen.css';

interface ProfileScreenProps {
  onBack: () => void;
}

const PROFILE_ROLES = ['Разработчик', 'Инвестор', 'Заказчик', 'Наблюдатель'] as const;

export function ProfileScreen({ onBack }: ProfileScreenProps) {
  const { profile, loading, updateBio, updateRole } = useProfile();
  const [editingBio, setEditingBio] = useState(false);
  const [bioText, setBioText] = useState('');
  const [savingBio, setSavingBio] = useState(false);
  const bioSaveInFlightRef = useRef(false);
  const profileCompletion = calculateProfilePercent(profile);
  const animatedCompletion = clampPercent(useCountUp(profileCompletion, 1100));
  const level = profile ? normalizeLevel(profile.level) : 1;

  const animatedProjects = useCountUp(profile?.project_count ?? 0, 900);
  const animatedRequests = useCountUp(profile?.requests_count ?? 0, 980);
  const animatedStreak = useCountUp(profile?.streak_days ?? 0, 1060);

  useBackButton(onBack);

  useEffect(() => {
    if (!editingBio) {
      setBioText(profile?.bio || '');
    }
  }, [editingBio, profile?.bio]);

  if (loading) {
    return (
      <div className="profile-screen">
        <Skeleton width={60} height={60} borderRadius={20} />
        <Skeleton width="50%" height={16} style={{ marginTop: 12 }} />
        <Skeleton width="35%" height={12} style={{ marginTop: 6 }} />
      </div>
    );
  }

  if (!profile) return null;

  const completenessHint = getCompletenessHint(profile);
  const beginBioEdit = () => {
    haptic.tap();
    setBioText(profile.bio || '');
    setEditingBio(true);
  };

  const finishBioEdit = async () => {
    if (bioSaveInFlightRef.current) return;

    const nextBio = bioText;
    const currentBio = profile.bio || '';

    if (nextBio === currentBio) {
      setEditingBio(false);
      return;
    }

    bioSaveInFlightRef.current = true;
    setSavingBio(true);

    try {
      await updateBio(nextBio);
      setEditingBio(false);
      haptic.success();
    } catch {
      haptic.error();
    } finally {
      bioSaveInFlightRef.current = false;
      setSavingBio(false);
    }
  };

  return (
    <div className="profile-screen">
      {/* Centered hero */}
      <div className="profile-hero">
        <div className="profile-hero-avatar">
          <AvatarWithProgress
            avatarUrl={profile.avatar_url}
            progress={profileCompletion}
            level={level}
          />
        </div>
        <h2 className="profile-hero-name">{profile.full_name || 'Аноним'}</h2>
        <span className="profile-hero-username">
          {profile.username ? `@${profile.username}` : 'username не указан'}
        </span>
        {profile.selected_role ? (
          <span className="profile-role-pill">{profile.selected_role}</span>
        ) : (
          <span className="profile-role-pill profile-role-pill-empty">Роль не выбрана</span>
        )}
      </div>

      {/* Progress card with hint */}
      <div className="profile-progress-card profile-reveal" style={getRevealStyle(0)}>
        <div className="profile-progress-header" style={{ marginBottom: 6 }}>
          <span className="profile-progress-label">Профиль</span>
          <span className="profile-progress-value">{Math.round(animatedCompletion)}%</span>
        </div>
        <div className="profile-progress-bar-track profile-progress-bar">
          <div
            className="profile-progress-bar-fill profile-progress-fill"
            style={{ width: `${animatedCompletion}%` }}
          />
        </div>
        {completenessHint && (
          <div className="profile-progress-hint">{completenessHint}</div>
        )}
      </div>

      {/* Stats grid */}
      <div className="profile-stats profile-reveal" style={{ marginLeft: 20, marginRight: 20, ...getRevealStyle(1) }}>
        <div className="profile-stat">
          <div className="profile-stat-val">{Math.round(animatedProjects)}</div>
          <div className="profile-stat-label">Проектов</div>
        </div>
        <div className="profile-stat">
          <div className="profile-stat-val">{Math.round(animatedRequests)}</div>
          <div className="profile-stat-label">Заявок</div>
        </div>
        <div className="profile-stat">
          <div className="profile-stat-val">{Math.round(animatedStreak)}</div>
          <div className="profile-stat-label">Дней подряд</div>
        </div>
      </div>

      {/* Bio */}
      <div className="profile-section profile-reveal" style={getRevealStyle(2)}>
        <div className="profile-section-header section-label">
          <span>О себе</span>
          <button
            type="button"
            className="profile-edit-btn"
            data-bio-action={editingBio ? 'save' : 'edit'}
            disabled={savingBio}
            onClick={() => {
              if (editingBio) {
                void finishBioEdit();
                return;
              }
              beginBioEdit();
            }}
          >
            {editingBio ? 'Готово' : <IconPen size={14} />}
          </button>
        </div>
        {editingBio ? (
          <textarea
            className="profile-bio-input"
            value={bioText}
            onChange={(e) => setBioText(e.target.value)}
            disabled={savingBio}
            onBlur={(event) => {
              if (event.relatedTarget instanceof HTMLElement && event.relatedTarget.dataset.bioAction === 'save') {
                return;
              }
              void finishBioEdit();
            }}
            placeholder="Расскажи о себе и своём стеке..."
            maxLength={500}
            autoFocus
          />
        ) : (
          <div className="profile-bio-text">
            {profile.bio || 'Добавь описание о себе для +30%'}
          </div>
        )}
      </div>

      <div className="profile-section profile-reveal" style={getRevealStyle(3)}>
        <div className="profile-section-header section-label">Роль</div>
        <div className="profile-role-options">
          {PROFILE_ROLES.map((role) => {
            const selected = profile.selected_role === role;
            return (
              <button
                key={role}
                type="button"
                className={`profile-role-option${selected ? ' selected' : ''}`}
                onClick={async () => {
                  haptic.tap();
                  if (selected) {
                    await updateRole('');
                    return;
                  }
                  await updateRole(role);
                }}
              >
                {role}
              </button>
            );
          })}
        </div>
      </div>

      {/* Streak */}
      <div className="profile-reveal" style={getRevealStyle(4)}>
        <StreakWidget streakDays={profile.streak_days} />
      </div>

      {/* Badges */}
      <div className="profile-reveal" style={getRevealStyle(5)}>
        <BadgesList badges={profile.badges} />
      </div>

      {/* Settings entry */}
      <div className="profile-section profile-reveal" style={getRevealStyle(6)}>
        <div className="profile-section-header section-label">Настройки</div>
        <button
          type="button"
          className="profile-settings-row"
          onClick={() => {
            haptic.tap();
            useAppStore.getState().setScreen('settings');
          }}
        >
          <span className="profile-settings-row-label">Уведомления, рефералы, поддержка Stars</span>
          <IconChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}

function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, value));
}

function calculateProfilePercent(profile: GamificationProfile | null | undefined): number {
  return clampPercent(profile?.completion_percent ?? profile?.profile_completeness ?? 0);
}

function useCountUp(targetValue: number, durationMs: number = 900): number {
  const [value, setValue] = useState(0);

  useEffect(() => {
    const target = Math.max(0, targetValue);

    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setValue(target);
      return;
    }

    let raf = 0;
    let startTime: number | null = null;

    const animate = (time: number) => {
      if (startTime === null) startTime = time;
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / durationMs, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);

      if (progress < 1) {
        raf = window.requestAnimationFrame(animate);
      }
    };

    setValue(0);
    raf = window.requestAnimationFrame(animate);

    return () => window.cancelAnimationFrame(raf);
  }, [durationMs, targetValue]);

  return value;
}

function getRevealStyle(index: number): CSSProperties {
  return { ['--profile-reveal-delay' as string]: `${index * 70}ms` };
}

function normalizeLevel(level: number): 1 | 2 | 3 | 4 {
  if (level >= 4) return 4;
  if (level >= 3) return 3;
  if (level >= 2) return 2;
  return 1;
}

function getCompletenessHint(profile: GamificationProfile): string | null {
  return profile.next_completion_hint || null;
}
